// Convertit des heures décimales en minutes entières (évite les erreurs d'arrondi flottant)
export function hoursToMinutes(time: number) {
  return Math.round(time * 60);
}

// Heures décimales -> "HH:mm", sans limite à 24h et signé en cas de dépassement (ex: -0.5 -> "-00:30")
export function numberToHour(time: number) {
  const totalMinutes = hoursToMinutes(time || 0);
  const sign = totalMinutes < 0 ? "-" : "";
  const absMinutes = Math.abs(totalMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function dateToSeconds(date:any){
  const dateObj = new Date(date);
  const seconds = Math.floor(dateObj.getTime() / 1000); // Convertir en secondes
  const nanoseconds = (dateObj.getTime() % 1000) * 1000000; // Convertir en nanosecondes
  const result = { seconds, nanoseconds };
  return result;
}

// Clé d'un jour calendaire (heure locale), pour comparer des dates au jour près
export function dayKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

// Adresse saisie -> origine du site et domaine sans `www`, qui identifie le site. null si l'adresse n'est pas valide
export function siteDepuisAdresse(adresse: string) {
  const saisie = (adresse || "").trim();
  try {
    const { protocol, host, hostname } = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(saisie) ? saisie : `https://${saisie}`);
    if (!/^https?:$/.test(protocol) || !/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(hostname)) {
      return null;
    }
    return { url: `${protocol}//${host}`.toLowerCase(), domaine: hostname.toLowerCase().replace(/^www\./, "") };
  } catch {
    return null;
  }
}

export const CMS_LABELS: Record<SiteCms, string> = { wordpress: "WordPress", joomla: "Joomla", autre: "Autre" };
export const SAUVEGARDE_LABELS: Record<SiteSauvegarde, string> = {
  akeeba: "Akeeba",
  autre: "Autre méthode",
  aucune: "Non prévue",
};

// Un site sauvegardé hors Akeeba, ou sans sauvegarde au contrat : son état n'est pas suivi, et ce n'est pas une alerte
export function sauvegardeHorsAkeeba(site: Pick<SiteType, "sauvegarde">) {
  return site.sauvegarde === "autre" || site.sauvegarde === "aucune";
}

// Noms des sites, dans l'ordre de la liste d'identifiants ; vide si aucun n'est connu
export function nomsDesSites(idsites: string[] | undefined, sites: SiteType[]) {
  return (idsites ?? [])
    .map((id) => sites.find((site) => site.id === id)?.nom)
    .filter(Boolean)
    .join(", ");
}

const memeListe = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

// CMS proposé pour des sites : l'e-mail écrit « Mise à jour {{cms}} », donc « Autre » n'est jamais proposé
export function cmsDesSites(sites: SiteType[]) {
  const connus = sites.filter((site) => site.cms !== "autre").map((site) => CMS_LABELS[site.cms] ?? site.cms);
  return Array.from(new Set(connus)).join(", ");
}

// Champs d'une intervention qui dépendent de ses sites. Sans site sur le forfait, les champs texte saisis restent.
// `site_internet` et `cms` sont une copie, utilisée par l'e-mail. Le CMS reste celui du formulaire. Le texte du site
// d'une intervention existante n'est réécrit que si sa sélection de sites change, pour garder l'historique
export function champsSitesIntervention(
  values: { idsites?: string[]; site_internet?: string; cms?: string },
  sites: SiteType[],
  parDefaut: string[],
  existe: boolean
) {
  if (sites.length === 0) {
    return { site_internet: values.site_internet, cms: values.cms };
  }
  const idsites = sites.map((site) => site.id).filter((id) => (values.idsites ?? []).includes(id));
  const choisis = sites.filter((site) => idsites.includes(site.id));
  const cms = (values.cms ?? "").trim() || cmsDesSites(choisis);
  if (existe && memeListe(idsites, parDefaut)) {
    return { idsites, cms };
  }
  return { idsites, site_internet: choisis.map((site) => site.nom).join(", "), cms };
}

// Sites cochés qu'un autre forfait actif couvre déjà
export function sitesDejaCouverts(idsites: string[], forfaits: ForfaitType[], idForfait?: string) {
  const autres = forfaits.filter((forfait) => forfait.statut === true && forfait.id !== idForfait);
  return idsites.filter((id) => autres.some((forfait) => forfait.idsites?.includes(id)));
}

// Début de la ligne de l'e-mail : une intervention sans plugins ni sauvegarde n'est pas une mise à jour (support, par exemple)
export function actionIntervention(intervention: { plugins?: boolean; sauvegarde?: boolean }) {
  return intervention.plugins || intervention.sauvegarde ? "Mise à jour" : "Intervention";
}
